import axios from "axios";
import { ChevronDown, Play, RotateCcw, Trophy, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "./GameFullscreenButton";
import { toast } from "../components/ui/sonner";
import { awardFeat, awardPoints, getLinkedTag, getTubeCount, spendTube } from "../utils/rewards";
import { getCachedUser } from "../utils/auth";
import { API, arenaSrc, CPU_TIERS, floorSrc, freshMatch, H, IMG_SPRITE_TEAMS, MAX_Y, MIN_Y, TEAM_ORDER, TEAMS, teamToFranchise, timeAgo, W } from "./riotball/constants";
import { buildPlayoff, advanceByes, simSiblingPlayIn, PLAYOFF_CHAMPION_PTS, PLAYOFF_ROUNDS, playerOpponent, resolvePlayerMatch, ROUND_TIERS, simToEnd } from "./riotball/playoff";
import { makeThrowOrb, stepMatch } from "./riotball/engine";
import { renderMatch } from "./riotball/render";
import { RiotballPlayoffBracket } from "./RiotballPlayoffBracket";

export function RiotballGame() {
  const canvasRef = useRef(null);
  const sheetRef = useRef(null);
  const refSpritesRef = useRef(null);
  const teamSpritesRef = useRef(null);
  const arenaRef = useRef({});
  const floorRef = useRef({});
  const hudRef = useRef(null);       // DOM scoreboard above the canvas — updated per frame, no re-renders
  const bannerLineRef = useRef(null); // DOM banner line — keeps big words off the court
  const stateRef = useRef(freshMatch());
  const phaseRef = useRef("select");
  const teamRef = useRef("red");
  const rivalRef = useRef("white");
  const keysRef = useRef({});
  const sfxRef = useRef({});
  const [phase, setPhase] = useState("select");
  const [boostChoice, setBoostChoice] = useState(null);
  const [tubesLeft, setTubesLeft] = useState(() => getTubeCount());
  const [activeBoost, setActiveBoost] = useState(null);
  const [howtoOpen, setHowtoOpen] = useState(false);
  const [team, setTeam] = useState("red");
  const [rival, setRival] = useState("white");
  const [pendingSide, setPendingSide] = useState(null);
  const [matchMode, setMatchMode] = useState("solo");
  const [matchTier, setMatchTier] = useState("easy");
  // playoff bracket — 10 teams, you play your line, the mesh sims the rest
  const [playoff, setPlayoff] = useState(null);
  const [coronation, setCoronation] = useState(null);
  const playoffRef = useRef(null);
  const modeRef = useRef("solo");
  const [finalScore, setFinalScore] = useState(0);
  const [cpuFinal, setCpuFinal] = useState(0);
  const [newRecord, setNewRecord] = useState(false);
  const [sirenFlash, setSirenFlash] = useState(false);
  const [timeoutsLeft, setTimeoutsLeft] = useState(3);
  const [timeoutActive, setTimeoutActive] = useState(false);
  const timeoutFnRef = useRef(() => {});
  const [highlights, setHighlights] = useState([]);
  const [highs, setHighs] = useState(() =>
    Object.fromEntries(TEAM_ORDER.map((t) => [t, Number(localStorage.getItem(TEAMS[t].highKey) || 0)])),
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const sheet = new Image();
    sheet.src = "/assets/riotball-sprites.webp";
    sheet.onload = () => {
      sheetRef.current = sheet;
    };
    // the league referee — gold-striped official patrolling the near touchline
    const loadRef = (src) => {
      const img = new Image();
      img.src = src;
      return img;
    };
    refSpritesRef.current = {
      idle: loadRef("/assets/referee/idle.png"),
      foul: loadRef("/assets/referee/foul.png"),
      shield: loadRef("/assets/referee/shield.png"),
      run: [2, 3, 4, 5].map((n) => loadRef(`/assets/referee/run${n}.png`)),
    };
    // expansion franchises — pose-keyed PNGs sliced from their official sheets
    teamSpritesRef.current = Object.fromEntries(
      Object.entries(IMG_SPRITE_TEAMS).map(([key, poses]) => [
        key,
        Object.fromEntries(Object.entries(poses).map(([pose, src]) => [pose, loadRef(src)])),
      ]),
    );
    // home arenas — one painted backdrop per franchise behind the glass court
    arenaRef.current = Object.fromEntries(TEAM_ORDER.map((k) => [k, loadRef(arenaSrc(k))]));
    // neon court floors — painted inside the court lines
    floorRef.current = Object.fromEntries(TEAM_ORDER.map((k) => [k, loadRef(floorSrc(k))]));
    // league-wide records — server is the source of truth, local marks migrate upward
    let recordsAlive = true;
    const fetchHighlights = () =>
      axios
        .get(`${API}/riotball/highlights`)
        .then(({ data }) => {
          if (recordsAlive && Array.isArray(data.highlights)) setHighlights(data.highlights);
        })
        .catch(() => {});
    fetchHighlights();
    axios
      .get(`${API}/riotball/records`)
      .then(({ data }) => {
        if (!recordsAlive) return;
        setHighs(Object.fromEntries(TEAM_ORDER.map((t) => [t, data[t] || 0])));
        TEAM_ORDER.forEach((t) => {
          const local = Number(localStorage.getItem(TEAMS[t].highKey) || 0);
          if (local > (data[t] || 0)) {
            axios
              .post(`${API}/riotball/record`, { team: t, score: local })
              .then(({ data: lifted }) => {
                if (recordsAlive) {
                  setHighs(Object.fromEntries(TEAM_ORDER.map((k) => [k, lifted[k] || 0])));
                  window.dispatchEvent(new Event("riotball-records-changed"));
                }
              })
              .catch(() => {});
          }
        });
      })
      .catch(() => {});
    const mkSfx = (src, volume) => {
      const audio = new Audio(src);
      audio.preload = "auto";
      audio.volume = volume;
      return audio;
    };
    sfxRef.current = {
      throw: mkSfx("/assets/game-jump.wav", 0.4),
      goal: mkSfx("/assets/game-coin.wav", 0.5),
      blocked: mkSfx("/assets/gate-denied.wav", 0.4),
      heartbeat: mkSfx("/assets/heartbeat.wav", 0.55),
      crowd: mkSfx("/assets/crowd-roar.wav", 0.55),
      siren: mkSfx("/assets/record-siren.wav", 0.6),
    };
    const playSfx = (name, forceRate) => {
      const audio = sfxRef.current[name];
      if (!audio) return;
      // final ten seconds: tape-slow the arena like the worm game's slow-mo
      const stretched = phaseRef.current === "running" && stateRef.current.timeLeft <= 600;
      const rate = forceRate ?? (stretched ? 0.55 : 1);
      audio.playbackRate = rate;
      audio.preservesPitch = rate === 1;
      if ("webkitPreservesPitch" in audio) audio.webkitPreservesPitch = rate === 1;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    };

    const throwOrb = makeThrowOrb({ stateRef, phaseRef, playSfx, teamRef });

    const onKey = (event) => {
      keysRef.current[event.code] = true;
      if (["ArrowUp", "ArrowDown", "Space", "KeyW", "KeyS", "KeyT"].includes(event.code)) event.preventDefault();
      if (event.code === "Space") throwOrb();
      if (event.code === "KeyT") timeoutFnRef.current();
    };
    const onKeyUp = (event) => {
      keysRef.current[event.code] = false;
    };

    // touch: drag to move, quick tap to throw
    let touchStartY = null;
    let touchMoved = false;
    const onPointerDown = (event) => {
      event.preventDefault();
      touchStartY = event.clientY;
      touchMoved = false;
    };
    const onPointerMove = (event) => {
      if (touchStartY === null || phaseRef.current !== "running") return;
      const rect = canvas.getBoundingClientRect();
      const targetY = ((event.clientY - rect.top) / rect.height) * H;
      const g = stateRef.current;
      if (Math.abs(event.clientY - touchStartY) > 8) touchMoved = true;
      if (touchMoved) {
        g.playerY = Math.max(MIN_Y, Math.min(MAX_Y, targetY + 30));
        g.playerMoving = true;
      }
    };
    const onPointerUp = () => {
      if (touchStartY !== null && !touchMoved) throwOrb();
      touchStartY = null;
    };

    let raf = 0;
    const loop = () => {
      try {
      const g = stateRef.current;
      g.frame += 1;
      const teamKey = teamRef.current;
      const rival = rivalRef.current;

      if (phaseRef.current === "running") {
        stepMatch(g, { keysRef, playSfx, teamKey, fetchHighlights, setFinalScore, setCpuScore: setCpuFinal, setHighs, setNewRecord, setSirenFlash, phaseRef, setPhase, setActiveBoost, onFullTime: onPlayoffFullTime });
      }

      renderMatch(g, ctx, { sheetRef, phaseRef, teamKey, rival, refSpritesRef, teamSpritesRef, arenaRef, floorRef });

      // DOM HUD above the canvas — score, timer, heat pips and the banner line,
      // written straight to the nodes so React never re-renders per frame
      const hud = hudRef.current;
      if (hud) {
        const n = hud._n || (hud._n = {
          score: hud.querySelector('[data-hud="score"]'),
          timer: hud.querySelector('[data-hud="timer"]'),
          pips: hud.querySelectorAll('[data-hud="pip"]'),
        });
        if (n.score) n.score.textContent = g.mode === "vs" ? `YOU ${g.score} — ${g.cpuScore} CPU` : `GOALS ${g.score}`;
        if (n.timer) {
          n.timer.textContent = `${Math.ceil(g.timeLeft / 60)}S`;
          n.timer.classList.toggle("is-low", g.timeLeft < 600);
        }
        const heat = g.cpu?.heat || 0;
        n.pips.forEach((pip, i) => pip.classList.toggle("hot", i < heat));
      }
      const line = bannerLineRef.current;
      if (line) {
        const live = g.bannerTimer > 0 && phaseRef.current === "running" && g.banner;
        if (line._txt !== g.banner) {
          line.textContent = g.banner || "";
          line._txt = g.banner;
        }
        line.classList.toggle("is-live", !!live);
        line.classList.toggle("is-goal", !!live && g.banner.startsWith("GOAL"));
      }

      } catch (frameErr) {
        // never let one bad frame freeze the match — keep the loop alive
        console.error("riotball frame error", frameErr);
      }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);

    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    window.__riotball = { stateRef, phaseRef, teamRef, sfxRef };

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      recordsAlive = false;
      delete window.__riotball;
    };
  }, []);
  const startMatch = (chosen, opponent, opts = {}) => {
    const next = chosen || teamRef.current;
    teamRef.current = next;
    setTeam(next);
    const nextRival = opponent && opponent !== next ? opponent : rivalRef.current !== next ? rivalRef.current : TEAM_ORDER.find((t) => t !== next);
    rivalRef.current = nextRival;
    setRival(nextRival);
    const mode = opts.mode ?? matchMode;
    const tier = opts.tier ?? matchTier;
    modeRef.current = mode;
    stateRef.current = freshMatch(mode, tier);
    stateRef.current.playoffLeg = !!opts.playoffLeg; // only bracket legs resolve the playoff
    if (opts.banner) {
      stateRef.current.banner = opts.banner;
      stateRef.current.bannerTimer = 110;
    }
    // burn a stocked Bio Hack tube for a one-match boost
    if (boostChoice && spendTube()) {
      stateRef.current.tubeBoost = boostChoice;
      setActiveBoost(boostChoice);
      setTubesLeft(getTubeCount());
    } else {
      setActiveBoost(null);
    }
    phaseRef.current = "running";
    setPhase("running");
    setNewRecord(false);
    setTimeoutsLeft(3);
    setTimeoutActive(false);
  };

  /* ---------- PLAYOFFS — 10-team bracket, you play your line ---------- */
  const startPlayoff = async () => {
    const side = pendingSide || teamRef.current;
    // seed from the live season standings — the bracket mirrors the league table
    let records = null;
    try {
      const res = await fetch(`${API}/riotball/records`);
      if (res.ok) records = await res.json();
    } catch {
      records = null; // offline desk — fall back to the random draw
    }
    const bracket = advanceByes(simSiblingPlayIn(buildPlayoff(side, records)));
    playoffRef.current = bracket;
    setPlayoff(bracket);
    setCoronation(null);
    setMatchMode("vs");
    const opp = playerOpponent(bracket);
    const inPlayIn = bracket.roundIdx === 0;
    const seedNote = inPlayIn ? `PLAY-IN — #${bracket.playerSeed} SEED` : `#${bracket.playerSeed} SEED — QUARTERFINAL BYE`;
    startMatch(side, opp, {
      mode: "vs",
      tier: ROUND_TIERS[bracket.roundIdx],
      playoffLeg: true,
      banner: `${seedNote} VS ${TEAMS[opp]?.name}`,
    });
  };

  const advancePlayoff = () => {
    const p = playoffRef.current;
    const opp = playerOpponent(p);
    if (!opp) return;
    startMatch(p.playerTeam, opp, {
      mode: "vs",
      tier: ROUND_TIERS[p.roundIdx],
      playoffLeg: true,
      banner: `${PLAYOFF_ROUNDS[p.roundIdx]} — ${TEAMS[opp]?.name} ACROSS THE LINE`,
    });
  };

  const simPlayoffOut = () => {
    const next = simToEnd(playoffRef.current);
    playoffRef.current = next;
    setPlayoff(next);
  };

  const dropBracket = () => {
    playoffRef.current = null;
    setPlayoff(null);
    setCoronation(null);
    phaseRef.current = "select";
    setPhase("select");
  };

  // full-time during a bracket run — record your match, the mesh sims the rest
  const onPlayoffFullTime = (g) => {
    const p = playoffRef.current;
    if (!p || p.finished || !g.playoffLeg) return;
    const next = resolvePlayerMatch(p, g.score, g.cpuScore);
    playoffRef.current = next;
    setPlayoff(next);
    if (next.finished && next.champion === p.playerTeam) {
      awardPoints("riotball", PLAYOFF_CHAMPION_PTS);
      awardFeat("PLAYOFF CHAMPION");
      // the league remembers every chip — record the run on the mesh
      const finalMatch = next.rounds[next.rounds.length - 1][0];
      const finalist = finalMatch.home === p.playerTeam ? finalMatch.away : finalMatch.home;
      fetch(`${API}/riotball/playoff-champions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          team: teamToFranchise(p.playerTeam),
          finalist: finalist ? teamToFranchise(finalist) : null,
          player_goals: g.score,
          finalist_goals: g.cpuScore,
          name: getCachedUser()?.name || null,
          tag: getLinkedTag(),
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d?.dynasty) toast.success(`${d.dynasty} — THREE CHIPS, ONE FRANCHISE. THE LEAGUE BOWS.`);
          if (d?.dynasty || d?.back_to_back) setCoronation({ dynasty: d.dynasty || null, backToBack: Boolean(d.back_to_back) });
        })
        .catch(() => {});
    }
  };

  // three timeouts per match — freeze the clock and the keeper, resume on your call
  const toggleTimeout = () => {
    if (phase !== "running") return;
    const g = stateRef.current;
    const whistle = () => {
      try {
        const a = new Audio("/assets/record-siren.wav");
        a.volume = 0.4;
        a.play().catch(() => {});
      } catch {}
    };
    if (timeoutActive) {
      g.timeout = false;
      setTimeoutActive(false);
      return;
    }
    if (timeoutsLeft <= 0 || g.celebrateTimer > 0) return;
    g.timeout = true;
    setTimeoutsLeft((n) => n - 1);
    setTimeoutActive(true);
    whistle();
  };
  timeoutFnRef.current = toggleTimeout;

  const boostPicker = (
    <div className="tube-boost" data-testid="riotball-tube-boost-picker">
      <span>BIO HACK TUBES: {tubesLeft} — BURN ONE FOR THIS MATCH</span>
      <div className="tube-boost-row">
        <button
          type="button"
          className={`tube-boost-option ${boostChoice === "sprint" ? "is-armed" : ""}`}
          onClick={() => setBoostChoice(boostChoice === "sprint" ? null : "sprint")}
          data-testid="riotball-tube-boost-sprint"
        >
          <Zap size={13} /> SPRINT BURST
        </button>
        <button
          type="button"
          className={`tube-boost-option ${boostChoice === "power" ? "is-armed" : ""}`}
          onClick={() => setBoostChoice(boostChoice === "power" ? null : "power")}
          data-testid="riotball-tube-boost-power"
        >
          <Zap size={13} /> POWER THROW
        </button>
      </div>
    </div>
  );

  return (
    <div className="riotball-game" data-testid="riotball-game">
      <GameFullscreenButton testid="riotball-fullscreen" />
      <div className="arena-stage" data-testid="arena-stage">
        <div className="arena-stage-top">
          <span className="arena-live" data-testid="arena-live-badge">
            <i /> LIVE<span className="arena-live-word">&nbsp;TRANSMISSION</span>
          </span>
          <span className="arena-channel">CHANNEL 2355 // TRAINING ARENA</span>
          <span className={`arena-scoreboard ${sirenFlash ? "siren-flash" : ""}`} data-testid="arena-scoreboard">
            <img className="arena-scorebug-logo" src={TEAMS[team].emblem} alt={TEAMS[team].name} data-testid="scorebug-logo-side" />
            <b style={{ color: TEAMS[team].color }}>{TEAMS[team].short} {highs[team] || 0}</b>
            <em>—</em>
            <b style={{ color: TEAMS[rival].color }}>{highs[rival] || 0} {TEAMS[rival].short}</b>
            <img className="arena-scorebug-logo" src={TEAMS[rival].emblem} alt={TEAMS[rival].name} data-testid="scorebug-logo-rival" />
          </span>
        </div>
        <div className={`game-frame ${phase === "select" ? "is-select" : ""}`}>
        {phase === "running" ? (
          <>
            <div
              className="riotball-hud"
              ref={hudRef}
              data-testid="riotball-hud"
              style={{ "--team-color": TEAMS[teamRef.current]?.color, "--rival-color": TEAMS[rivalRef.current]?.color }}
            >
              <div className="riotball-hud-side">
                <span className="riotball-hud-team">{TEAMS[teamRef.current]?.name}</span>
                <strong className="riotball-hud-score" data-hud="score" data-testid="riotball-hud-score">
                  {matchMode === "vs" ? "YOU 0 — 0 CPU" : "GOALS 0"}
                </strong>
                <span className="riotball-hud-trait">TRAIT — {TEAMS[teamRef.current]?.trait.name}</span>
                {matchMode === "vs" ? (
                  <span className="riotball-hud-heat" data-testid="riotball-hud-heat">
                    CPU HEAT
                    {[...Array(Math.max(CPU_TIERS[matchTier]?.combos || 1, 1))].map((_, i) => (
                      <i key={i} data-hud="pip" />
                    ))}
                  </span>
                ) : null}
              </div>
              <strong className="riotball-hud-timer" data-hud="timer" data-testid="riotball-hud-timer">60S</strong>
            </div>
            <div className="riotball-bannerline" ref={bannerLineRef} data-testid="riotball-bannerline" aria-live="polite" />
          </>
        ) : null}
        <canvas ref={canvasRef} width={W} height={H} data-testid="riotball-canvas" aria-label="AL Riotball training arena" />

        {phase === "running" && activeBoost ? (
          <div className="tube-hud-chip" data-testid="riotball-tube-hud-chip">
            <Zap size={11} /> BIO HACK — {activeBoost === "sprint" ? "SPRINT BURST" : "POWER THROW"}
          </div>
        ) : null}

        {phase === "select" ? (
          <div className="game-overlay" data-testid="riotball-team-select">
            <span>TRAINING ARENA // 2355 SEASON</span>
            <span className="riotball-play-now" data-testid="riotball-play-now">▶ PLAY NOW ◀</span>
            <h2>{pendingSide ? "PICK YOUR OPPONENT" : "PICK YOUR SIDE"}</h2>
            <p>
              {pendingSide
                ? `${TEAMS[pendingSide].name} is your side — now choose who dares to face them.`
                : "All ten franchises on tour. Move with W/S or drag. Throw with SPACE or tap. Beat the moving goal — 60 seconds on the clock."}
            </p>
            {tubesLeft > 0 && !pendingSide ? boostPicker : null}
            {pendingSide ? (
              <div className="riotball-mode-picks" data-testid="riotball-mode-picks">
                <button
                  type="button"
                  className={`riotball-mode-pick${matchMode === "solo" && !playoff ? " is-active" : ""}`}
                  onClick={() => { setMatchMode("solo"); playoffRef.current = null; setPlayoff(null); }}
                  data-testid="riotball-mode-solo"
                >
                  SOLO DRILL
                </button>
                {Object.entries(CPU_TIERS).map(([tierKey, t]) => (
                  <button
                    type="button"
                    key={tierKey}
                    className={`riotball-mode-pick${matchMode === "vs" && matchTier === tierKey && !playoff ? " is-active" : ""}`}
                    onClick={() => { setMatchMode("vs"); setMatchTier(tierKey); playoffRef.current = null; setPlayoff(null); }}
                    data-testid={tierKey === "easy" ? "riotball-mode-vs" : `riotball-mode-${tierKey}`}
                  >
                    VS CPU — {t.label}{tierKey === "easy" ? " · THEY SCORE, YOU STEAL" : tierKey === "normal" ? " · FASTER CARRIER" : " · BANK SHOTS"}
                  </button>
                ))}
                <button
                  type="button"
                  className={`riotball-mode-pick is-playoff${playoff ? " is-active" : ""}`}
                  onClick={startPlayoff}
                  data-testid="riotball-mode-playoff"
                >
                  PLAYOFFS — 10-TEAM BRACKET TO THE CHIP
                </button>              </div>
            ) : null}
            {!pendingSide ? (
              <div className="riotball-team-picks" data-testid="riotball-side-picks">
                {TEAM_ORDER.map((key) => (
                  <button
                    type="button"
                    key={key}
                    className="riotball-team-pick pick-any"
                    style={{ "--team-color": TEAMS[key].color }}
                    onClick={() => setPendingSide(key)}
                    data-testid={`riotball-side-${key}`}
                  >
                    <img src={TEAMS[key].emblem} alt={`${TEAMS[key].name} emblem`} />
                    <strong>{TEAMS[key].name}</strong>
                    <small>BEST {highs[key] || 0}</small>
                  </button>
                ))}
              </div>
            ) : (
              <div className="riotball-team-picks" data-testid="riotball-opponent-picks">
                {TEAM_ORDER.filter((key) => key !== pendingSide).map((key) => (
                  <button
                    type="button"
                    key={key}
                    className="riotball-team-pick pick-any"
                    style={{ "--team-color": TEAMS[key].color }}
                    onClick={() => {
                      setPendingSide(null);
                      startMatch(pendingSide, key);
                    }}
                    data-testid={`riotball-opponent-${key}`}
                  >
                    <img src={TEAMS[key].emblem} alt={`${TEAMS[key].name} emblem`} />
                    <strong>{TEAMS[key].name}</strong>
                    <small>BEST {highs[key] || 0}</small>
                  </button>
                ))}
                <button
                  type="button"
                  className="riotball-team-pick pick-any pick-back"
                  onClick={() => setPendingSide(null)}
                  data-testid="riotball-side-back"
                >
                  ← CHANGE SIDE
                </button>
              </div>
            )}
          </div>
        ) : null}

        {timeoutActive && phase === "running" ? (
          <div className="game-overlay timeout-overlay" data-testid="riotball-timeout-overlay">
            <span>REF'S WHISTLE</span>
            <h2>TIME OUT</h2>
            <p>The clock is frozen. The keeper is frozen. Catch your breath and line up the play.</p>
            <p className="timeout-left" data-testid="riotball-timeouts-left">TIMEOUTS LEFT — {timeoutsLeft}</p>
            <button type="button" className="button button-primary" onClick={toggleTimeout} data-testid="riotball-resume-button">
              <Play size={15} /> RESUME PLAY
            </button>
          </div>
        ) : null}

        {phase === "over" ? (
          <div className="game-overlay" data-testid="riotball-over-screen">
            <span className="game-over-tag">
              <Trophy size={14} /> FULL TIME
            </span>
            <h2 data-testid="riotball-final-score">{finalScore} {finalScore === 1 ? "GOAL" : "GOALS"}</h2>
            {matchMode === "vs" ? (
              <p className={`riotball-verdict ${finalScore > cpuFinal ? "verdict-win" : finalScore < cpuFinal ? "verdict-loss" : ""}`} data-testid="riotball-verdict">
                {finalScore > cpuFinal ? `VICTORY — YOU ${finalScore} : ${cpuFinal} CPU (${CPU_TIERS[matchTier]?.label})` : finalScore < cpuFinal ? `DEFEAT — CPU ${cpuFinal} : ${finalScore} YOU (${CPU_TIERS[matchTier]?.label})` : `DRAW — ${finalScore} : ${cpuFinal} (${CPU_TIERS[matchTier]?.label})`}
              </p>
            ) : null}
            {matchMode === "vs" && stateRef.current?.steals > 0 ? (
              <p className="riotball-steal-bonus" data-testid="riotball-steal-bonus">
                LANE READS ×{stateRef.current.steals} — +{stateRef.current.steals * 2} PTS PICK-OFF BONUS
              </p>
            ) : null}
            {matchMode === "vs" && stateRef.current?.intercepts > 0 ? (
              <p className="riotball-steal-bonus" data-testid="riotball-intercept-bonus">
                LANE CUTS ×{stateRef.current.intercepts} — +{stateRef.current.intercepts * 3} PTS INTERCEPT BONUS
              </p>
            ) : null}
            {matchMode === "vs" && finalScore > cpuFinal && (CPU_TIERS[matchTier]?.winBonus || 0) > 0 ? (
              <p className="riotball-steal-bonus riotball-tier-bounty" data-testid="riotball-tier-bounty">
                {CPU_TIERS[matchTier].label} CONQUERED — +{CPU_TIERS[matchTier].winBonus} PTS BOUNTY
              </p>
            ) : null}
            <p className="fulltime-teams" data-testid="riotball-fulltime-teams">
              <img src={TEAMS[team].emblem} alt={TEAMS[team].name} className="fulltime-logo" data-testid="fulltime-logo-side" />
              <span style={{ color: TEAMS[team].color }}>{TEAMS[team].name}</span>
              <em>VS</em>
              <span style={{ color: TEAMS[rival].color }}>{TEAMS[rival].name}</span>
              <img src={TEAMS[rival].emblem} alt={TEAMS[rival].name} className="fulltime-logo" data-testid="fulltime-logo-rival" />
            </p>
            {newRecord ? (
              <p className="riotball-new-record" data-testid="riotball-new-record">
                <Zap size={14} /> NEW {TEAMS[team].name} RECORD
              </p>
            ) : (
              <p>LEAGUE BEST — {TEAMS[team].name}: {highs[team] || 0}</p>
            )}
            <small>THE LEAGUE REMEMBERS EVERY STRIKE.</small>
            {playoff ? (
              <div className="playoff-over" data-testid="playoff-over">
                <p className="playoff-round-result" data-testid="playoff-round-result">
                  {playoff.finished
                    ? playoff.champion === playoff.playerTeam
                      ? `CHAMPIONS — ${TEAMS[playoff.playerTeam]?.name} TAKE THE CHIP · +${PLAYOFF_CHAMPION_PTS} PTS`
                      : `${TEAMS[playoff.champion]?.name} TAKE THE CHIP — NEXT SEASON, RUNNER`
                    : playoff.playerAlive
                      ? `${PLAYOFF_ROUNDS[playoff.roundIdx]} NEXT — SURVIVE AND ADVANCE`
                      : "ELIMINATED — THE BRACKET MOVES ON"}
                </p>
                {playoff.lastUpsets?.length ? (
                  <div className="playoff-upsets" data-testid="playoff-upsets">
                    {playoff.lastUpsets.map((u, i) => (
                      <p key={i} className="playoff-upset" data-testid={`playoff-upset-${i}`}>
                        <Zap size={12} /> UPSET — #{u.winnerSeed} {TEAMS[u.winner]?.name} STUN #{u.loserSeed} {TEAMS[u.loser]?.name} {u.winnerGoals}–{u.loserGoals}
                      </p>
                    ))}
                  </div>
                ) : null}
                <small className="playoff-bracket-pointer">LIVE CHAMPIONSHIP BRACKET — BELOW THE ARENA ↓</small>
                {!playoff.finished && playoff.playerAlive ? (
                  <button type="button" className="button button-primary" onClick={advancePlayoff} data-testid="playoff-advance-button">
                    <Play size={15} /> ADVANCE — {PLAYOFF_ROUNDS[playoff.roundIdx]} VS {TEAMS[playerOpponent(playoff)]?.name}
                  </button>
                ) : null}
                {!playoff.finished && !playoff.playerAlive ? (
                  <button type="button" className="button button-primary" onClick={simPlayoffOut} data-testid="playoff-sim-button">
                    <Zap size={15} /> SIM THE REST OF THE BRACKET
                  </button>
                ) : null}
                <button type="button" className="game-resume" onClick={() => { dropBracket(); setPendingSide(null); }} data-testid="playoff-new-button">
                  <RotateCcw size={13} /> NEW BRACKET
                </button>
              </div>
            ) : (
              <>
                <button type="button" className="button button-primary" onClick={() => startMatch()} data-testid="riotball-rematch-button">
                  <Play size={15} /> REMATCH
                </button>
                <button type="button" className="game-resume" onClick={() => { setPendingSide(null); phaseRef.current = "select"; setPhase("select"); }} data-testid="riotball-change-team-button">
                  <RotateCcw size={13} /> CHANGE TEAM
                </button>
              </>
            )}
          </div>
        ) : null}
        </div>
        <div className="arena-stage-bottom">
          <button
            type="button"
            className="arena-chip arena-timeout-btn"
            onClick={toggleTimeout}
            disabled={phase !== "running" || (timeoutsLeft === 0 && !timeoutActive)}
            data-testid="riotball-timeout-button"
          >
            {timeoutActive ? "▶ RESUME PLAY" : `TIME OUT ${"●".repeat(timeoutsLeft)}${"○".repeat(3 - timeoutsLeft)}`}
          </button>
          <span className="arena-chip" data-testid="arena-chip-timer">60S MATCHES</span>
          <span className="arena-chip" data-testid="arena-chip-goal">2X E-GOAL SPEED</span>
          <span className="arena-chip" data-testid="arena-chip-squad">5 V 5 — ZIP PASSES BEAT MARKERS</span>
          <span className="arena-chip" data-testid="arena-chip-walls">LIVE WALLS — BANK SHOTS COUNT</span>
          <span className="arena-chip" data-testid="arena-chip-records">LEAGUE RECORDS SAVED</span>
        </div>
        <div className="game-howto" data-testid="riotball-howto">
          <button
            type="button"
            className={`game-howto-toggle ${howtoOpen ? "is-open" : ""}`}
            onClick={() => setHowtoOpen((v) => !v)}
            data-testid="riotball-howto-toggle"
          >
            HOW TO PLAY // LEAGUE RULES
            <ChevronDown size={14} />
          </button>
          {howtoOpen ? (
            <div className="game-howto-grid" data-testid="riotball-howto-panel">
              {[
                ["MOVE", "W / S or ↑ / ↓ on keyboard — drag up and down on touch."],
                ["THROW", "SPACE or a quick tap. Lead the moving electronic goal."],
                ["THE SQUAD", "Full 5 v 5 — every squadmate is man-marked by a white shirt. Lazy throws get marked down; thread zip passes through your squad and every one-touch gets faster. A two-touch zip is too hot for the keeper to block."],
                ["LIVE WALLS", "Top, bottom and back walls are live — bank the ball off them at an angle to wrong-foot the keeper."],
                ["THE GOAL", "The e-goal rotates and drifts at double speed; a defender tracks it at league speed."],
                ["THE REFEREE", "The gold-striped official paces the near touchline — arm up for stoppages, shield high for goals and timeouts. He never steps into the lanes."],
                ["FRANCHISE TRAITS", "Every kit carries its lore edge — Resilience reloads, Ghost decoys, Night Shift darkness, the Machine Bloom ramp. The banner calls it when it fires."],
                ["VS MODE", "The CPU takes the ball and drives at YOUR goal across three tiers — Easy shoots straight, Normal runs a faster carrier with a tighter steal window, Brutal barely telegraphs and banks shots off the walls. Track the carrier's lane to pick the ball clean off them (a white STEAL ring means your read is armed), or slide in to catch the shot. Win on Normal or Brutal and a PTS bounty lands on top — the bruises pay."],
                ["THE CLOCK", "60 seconds per match — most goals wins."],
                ["TIME OUTS", "Three per match. Hit T or the TIME OUT chip to freeze the clock, the goal and the keeper — resume when you've lined up the play."],
                ["RECORDS", "League bests are saved per side. Beat them and the ticker sings your name."],
              ].map(([term, desc]) => (
                <div key={term} className="game-howto-row">
                  <strong>{term}</strong>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="arena-ticker" data-testid="arena-ticker">
        <span className="arena-ticker-label">LATEST RESULTS //</span>
        <div className="arena-ticker-viewport">
          {(() => {
            // live table leader pinned to the front of the results feed
            const leader = Object.entries(highs).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])[0];
            const feed = leader ? [{ live: true, team: leader[0], score: leader[1] }, ...highlights] : highlights;
            return feed.length ? (
              <div className="arena-ticker-track">
                {[...feed, ...feed].map((h, i) =>
                  h.live ? (
                    <span key={i} className="arena-ticker-item arena-ticker-live" data-testid={i === 0 ? "ticker-live-score" : undefined}>
                      <em className="ticker-live-dot" />LIVE — 1ST {TEAMS[h.team].name} {h.score}
                    </span>
                  ) : (
                    <span key={i} className="arena-ticker-item" style={TEAMS[h.team] ? { color: TEAMS[h.team].color } : undefined}>
                      {TEAMS[h.team]?.name || h.team.toUpperCase()} {h.score} — NEW LEAGUE RECORD · {timeAgo(h.at)}
                    </span>
                  ),
                )}
              </div>
            ) : (
              <span className="arena-ticker-item">NO RECORDS YET — BE THE FIRST TO SET ONE</span>
            );
          })()}
        </div>
      </div>
      {playoff ? <RiotballPlayoffBracket playoff={playoff} coronation={coronation} /> : null}
    </div>
  );
}
